"use client";
import type { DataPoint } from "@/app/(landing)/prices/AvgMinMaxChart";
import { Icon } from "@/components/icons";
import { cn } from "cn";
import { LazyMotion, domAnimation, m } from "motion/react";
import { parseAsString, useQueryState } from "nuqs";
import { useMemo } from "react";

export const BuyPageSearchParams = {
  GPU_TYPE: "gpu_type",
  TYPE: "type",
  DURATION_UNIT: "duration_unit",
} as const;

export type GPUInstanceType = "H100" | "H200" | "B200";

export function useGPUInstanceType() {
  return useQueryState(
    BuyPageSearchParams.GPU_TYPE,
    parseAsString.withDefault("H100"),
  ) as [GPUInstanceType, (value: GPUInstanceType) => void];
}

export function GpuInstanceTypeDropdown({
  pricesByHardwareType,
}: { pricesByHardwareType: Record<string, DataPoint[]> }) {
  const [gpuType, setGPUType] = useGPUInstanceType();

  const options = useMemo(() => {
    return Object.keys(pricesByHardwareType).filter(
      (sku) =>
        pricesByHardwareType[sku].length > 0 &&
        pricesByHardwareType[sku].filter((p) => p.avg > 0).length > 0,
    );
  }, [pricesByHardwareType]);

  if (options.length <= 1) {
    return <span className="tabular-nums">{gpuType} nodes</span>;
  }

  return (
    <LazyMotion features={domAnimation}>
      <span className="inline-grid grid-cols-1 w-fit group">
        <m.select
          aria-label="GPU type"
          value={gpuType}
          onChange={(e) => setGPUType(e.target.value as GPUInstanceType)}
          className={cn(
            "col-start-1 row-start-1 py-0 appearance-none outline-hidden",
            "bg-neutral-50 transition-colors hover:bg-neutral-200/70 rounded-md hover:border-neutral-300 shadow-border pl-2 pr-6",
          )}
        >
          {options.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </m.select>
        <Icon
          name="ChevronDown"
          className="col-start-1 row-start-1 pointer-events-none self-center justify-self-end size-5 mr-1 text-neutral-500 group-hover:text-neutral-600 transition-colors"
        />
      </span>
    </LazyMotion>
  );
}
